import React, { useEffect, useState } from 'react';
import '../institute-automation.css';
import { InstituteAutomationService, InstituteReport, InstituteReportComponent } from '../instituteAutomationService';

// Trim trailing decimal zeros and add % for percentages: "17698.0000" → "17698", "1.0000" (%) → "1%".
const fmtValue = (c: InstituteReportComponent): string => {
    if (c.value == null) return '—';
    const s = String(c.value);
    const num = /^-?\d+\.\d+$/.test(s) ? String(Number(s)) : s;
    return c.valueType === 'percentage' ? `${num}%` : num;
};

const fmtDate = (d: string | null): string => {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Institute-facing read-only report page. The backend scopes the data to the
 * logged-in institute (from the token) — there is no institute selector here.
 */
const InstituteReportView: React.FC = () => {
    const [data, setData] = useState<InstituteReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const d = await InstituteAutomationService.getMyLatestReport();
                if (!cancelled) setData(d);
            } catch (_) {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="iar main p-3 p-md-4 bg-light flex-1">
            <div className="max-1250 mx-auto" style={{ maxWidth: 960 }}>
                {loading ? (
                    <div className="text-center text-secondary py-5"><span className="spinner-border spinner-border-sm me-2" />Loading your report…</div>
                ) : error ? (
                    <div className="card border border-danger-subtle shadow-sm rounded-3 p-5 text-center bg-white text-secondary">
                        <div className="font-36 mb-2">⚠️</div>
                        <h6 className="font-14 font-600 text-dark mb-1">Couldn’t load your report</h6>
                        <p className="font-12 mb-0">Please try again in a little while.</p>
                    </div>
                ) : !data ? (
                    <div className="card border border-light shadow-sm rounded-3 p-5 text-center bg-white text-secondary">
                        <div className="font-36 mb-2">📭</div>
                        <h6 className="font-14 font-600 text-dark mb-1">No report available yet</h6>
                        <p className="font-12 mb-0">Your latest report will appear here once it has been approved.</p>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="rounded-3 p-4 mb-4 text-white shadow" style={{ background: 'linear-gradient(135deg, #1c2b3a 0%, #243447 100%)' }}>
                            <div className="font-11 text-white-50 mb-1">{data.report.name}</div>
                            <h3 className="mb-1 font-22 font-600 text-white">{data.institute.name ?? `Institute ${data.institute.id}`}</h3>
                            <div className="font-12 text-white-50">
                                {data.report.status === 'sent' && data.report.sentAt
                                    ? `Sent ${fmtDate(data.report.sentAt)}`
                                    : `Reporting period ending ${fmtDate(data.report.scheduledSendDate)}`}
                            </div>
                        </div>

                        {/* Sections */}
                        {data.sections.map((s) => (
                            <div className="card border border-light shadow-sm rounded-3 mb-4 overflow-hidden bg-white" key={s.id}>
                                <div className="card-header bg-white border-bottom py-3 px-4 font-14 font-600 text-dark">{s.name}</div>
                                <div className="card-body p-4 bg-light bg-opacity-25">
                                    <div className="row g-3">
                                        {s.components.map((c, i) => (
                                            <div className="col-12 col-sm-6 col-md-4" key={i}>
                                                <div className="card border border-light shadow-sm rounded-3 p-3 bg-white h-100">
                                                    <div className="text-secondary font-10 font-600 mb-1">{c.name}</div>
                                                    <div className="font-22 font-600 text-dark">{fmtValue(c)}</div>
                                                    {c.unit && <div className="font-11 text-secondary mt-1">{c.unit}</div>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className="text-center text-secondary font-11 py-3">Powered by Univariety</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default InstituteReportView;
